/**
 * Pure helpers for the Final gate screens: who may override, the reason rule,
 * reading the server's refusal, and the plain-language pieces shown beside
 * Approve and before a download. No React, no Firebase. The server decides
 * everything (functions/src/verification/finalOverride.ts); these mirror it only
 * to decide what to SHOW.
 */

import { finalStatusLabel } from '../../utils/finalStatusLabel';

export const OVERRIDE_REASON_MIN = 10;

/** Mirrors the server: platform admin, or org admin of the project's own org. */
export function canOverrideFinal(
  claims: Record<string, unknown> | null | undefined,
  projectOrgId: string | null | undefined,
): boolean {
  const t = claims ?? {};
  if (t.admin === true) return true;
  return (
    t.orgAdmin === true &&
    typeof t.orgId === 'string' &&
    t.orgId !== '' &&
    typeof projectOrgId === 'string' &&
    t.orgId === projectOrgId
  );
}

export function validateOverrideReason(
  input: string,
): { ok: true; reason: string } | { ok: false; message: string } {
  const reason = (input ?? '').trim();
  if (reason.length >= OVERRIDE_REASON_MIN) return { ok: true, reason };
  return {
    ok: false,
    message: `Write a reason of at least ${OVERRIDE_REASON_MIN} characters.`,
  };
}

export interface GateRefusal {
  code: string;
  message: string;
  serverCanOverride: boolean;
  checks: { title: string; count: number }[];
}

const GATE_CODES = ['incomplete', 'failing-checks', 'override-not-permitted', 'override-reason'];

/** The server's refusal to mark a document Final, or null for any other error. */
export function parseGateRefusal(e: unknown): GateRefusal | null {
  const err = e as { message?: unknown; details?: any } | null;
  const d = err?.details;
  if (!d || typeof d.code !== 'string' || !GATE_CODES.includes(d.code)) return null;
  const checks = Array.isArray(d.checks)
    ? d.checks
        .filter((c: any) => c && typeof c.title === 'string')
        .map((c: any) => ({ title: c.title as string, count: Number(c.count) || 0 }))
    : [];
  return {
    code: d.code,
    message: typeof err?.message === 'string' ? err.message : 'This document cannot be marked Final yet.',
    serverCanOverride: d.canOverride === true,
    checks,
  };
}

/** Lead sentence and tail for the refusal box; the checks themselves are the list. */
export function refusalBody(r: GateRefusal): { lead: string; tail: string } {
  if (r.checks.length === 0) return { lead: r.message, tail: '' };
  const i = r.message.indexOf('. ');
  const first = i === -1 ? r.message : r.message.slice(0, i + 1);
  const tail = i === -1 ? '' : r.message.slice(i + 2).trim();
  const colon = first.indexOf(':');
  return { lead: colon === -1 ? first : first.slice(0, colon + 1), tail };
}

/** Label and amber flag from the same override count. */
export function artifactStatusDisplay(
  status: string | null | undefined,
  override: { checks?: readonly unknown[]; count?: number } | null | undefined,
  fallback: string,
): { label: string; overridden: boolean } {
  const label = finalStatusLabel(status, override);
  if (label === null) return { label: fallback, overridden: false };
  return { label, overridden: label !== 'Final' };
}

/** Fail closed only on a definite permission refusal; anything else lets the download go on. */
export function downloadCheckErrorMessage(e: unknown): string | null {
  const code = (e as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code.endsWith('permission-denied')
    ? 'You do not have permission to download this document.'
    : null;
}

export const NON_ADMIN_NOTE =
  'An administrator of your organisation can approve this document anyway, with a recorded reason.';

export function gateRefusalView(
  r: GateRefusal,
  userCanOverride: boolean,
): { showOverride: boolean; note: string } {
  if (r.code === 'incomplete') return { showOverride: false, note: '' };
  // The server's own sentence already tells a non-admin to ask an administrator.
  if (!userCanOverride && /administrator/i.test(r.message)) return { showOverride: false, note: '' };
  if (userCanOverride && r.serverCanOverride) return { showOverride: true, note: '' };
  return { showOverride: false, note: NON_ADMIN_NOTE };
}

/** Null when the download may go ahead, otherwise the sentence to show. */
export function downloadRefusalMessage(
  res: { allowed?: boolean; reasons?: string[] } | null | undefined,
): string | null {
  if (res?.allowed) return null;
  const reasons = (res?.reasons ?? []).filter((s) => typeof s === 'string' && s);
  return reasons.length ? reasons.join(' ') : 'This document cannot be downloaded right now.';
}

function toDate(at: unknown): Date | null {
  if (at instanceof Date) return at;
  const a = at as { toDate?: () => Date; seconds?: number } | null;
  if (a && typeof a.toDate === 'function') return a.toDate();
  if (a && typeof a.seconds === 'number') return new Date(a.seconds * 1000);
  return null;
}

/** "Overridden by X on <date>. Reason: ..." for a tooltip or a line. */
export function overrideDetailLine(
  o: { by?: string; byName?: string; at?: unknown; reason?: string } | null | undefined,
): string {
  if (!o) return '';
  const when = toDate(o.at);
  const parts = [`Overridden by ${o.byName || 'an administrator'}${when ? ` on ${when.toLocaleDateString()}` : ''}.`];
  if (o.reason) parts.push(`Reason: ${o.reason}`);
  return parts.join(' ');
}
